import * as React from 'react';
import {connect} from 'react-redux';

import * as ramda from 'ramda';

import {safelyGetRelationshipFilterIds} from 'nutshell-core/filter';
import {Outcomes} from 'nutshell-core/outcomes';
import {Stages} from 'nutshell-core/stages';
import type {FilterObject, LeadStage, StageId} from 'nutshell-core/types';
import {VisualPipeline as VP} from 'nutshell-core/visual-pipeline';
import type {StageTransition} from 'nutshell-core/visual-pipeline';

import type {Direction} from 'shells/popover/popover-sort-deprecated';

import type {NutshellState} from '../../store';
import {getDefaultStagesetId} from '../../util/get-default-stageset-id';

import {StagesEmpty} from '../base/empty-states/stages-empty';
import {GetSessionUser} from '../core/get-session-user';
import {CustomizeLeadCardModal} from '../lead/lead-card-dynamic/customize-lead-card-modal';
import {DefaultPipelineEditCTA} from '../lead/shared/components';
// @ts-expect-error
import * as dashboardActions from '../master-dashboard/dashboard-actions';

import {VisualPipelineColumn} from './column/visual-pipeline-column';
import {VisualPipelineColumnHeader} from './column/visual-pipeline-column-header';
import {SortAndCustomizeButton} from './controls/sort-and-customize-button';
import {VisualPipelineCloseLeadBar} from './visual-pipeline-close-lead-bar';
import {VisualPipelineEmptyContainer} from './visual-pipeline-empty-container';

import styles from './visual-pipeline.module.css';

const mapStateToProps = function (state: NutshellState, ownProps: OwnProps): StateProps {
    // See if we have an active stageset filter
    const stagesetFilter = ownProps.filters.find(ramda.has('stageset'));
    const stagesetFilterIds = stagesetFilter ? safelyGetRelationshipFilterIds(stagesetFilter) : [];

    // If we _do_ have a stageset filter, get the first stageset's apiId from the filter,
    // otherwise, just use the defaultStagesetId from our store's state.
    const stagesetId = stagesetFilterIds.length ? stagesetFilterIds[0] : getDefaultStagesetId();

    return {
        isLoading: Stages.getIsLoading(state),
        isEmpty: VP.getDoesPipelineHaveNoLeads(state),
        isEmptyFiltered: VP.getDoesPipelineHaveNoFilteredResults(state),
        // @ts-expect-error
        stagesErrorMessage: Stages.getErrorMessage(state),
        stages: Stages.getStagesByStageset(stagesetId)(state),
        isCardBeingDragged: VP.getIsCardBeingDragged(state),
        activeStageTransition: VP.getActiveStageTransition(state),
        stagesWithMoreLeadsToLoad: VP.getStageIdsThatCanLoadMoreLeads(stagesetId)(state),
        isAnyColumnLoading: VP.getIsAnyColumnLoading(stagesetId)(state),
    };
};

// @ts-expect-error
const mapDispatchToProps = function (dispatch, ownProps: OwnProps): DispatchProps {
    return {
        fetchLeadsSchema: () => {
            dispatch(dashboardActions.fetchLeadsSchema());
        },
        fetchStages: () => {
            dispatch(Stages.fetchStages());
        },
        fetchOutcomes: () => {
            dispatch(Outcomes.fetchOutcomes());
        },
        onLoadMoreLeads: (stageIds: StageId[]) => {
            const query = {
                filter: ownProps.filters,
                sort: ownProps.sort.selected,
                q: ownProps.search,
            } as const;

            dispatch(VP.fetchMoreLeadsForStages(stageIds, query, true));
        },
        fetchLeadsByStage: (stageId: StageId) => {
            const query = {
                filter: ownProps.filters,
                sort: ownProps.sort.selected,
                q: ownProps.search,
            } as const;

            dispatch(VP.fetchLeadsByStage(stageId, query, true));
        },
    };
};

const SHOULD_LOAD_MORE_THRESHOLD = 250;
const CONTENT_LEFT_PADDING = 16;

type OwnProps = {
    width: number;
    height: number;
    filters: FilterObject[];
    sort: {
        field: string;
        // Not sure why Flow is making me make this optional. Its being passed
        // into the sorting popover, where the direction is optional, but it
        // has to be optional here even though it never will be.
        direction?: Direction;
        selected: string;
    };
    search?: string;
    onToggleSort: (arg1: string) => any;
    stagesetId: string | null | undefined;
    fieldsToDisplay: string[];
    isLoadingFields: boolean;
};

type StateProps = {
    stages: LeadStage[];
    isLoading: boolean;
    isEmpty: boolean;
    isEmptyFiltered: boolean;
    errorMessage?: string;
    isCardBeingDragged: boolean;
    activeStageTransition: StageTransition;
    stagesWithMoreLeadsToLoad: StageId[];
    isAnyColumnLoading: boolean;
};

type DispatchProps = {
    fetchLeadsSchema: () => any;
    fetchStages: () => any;
    fetchOutcomes: () => any;
    onLoadMoreLeads: (stageIds: StageId[]) => any;
    fetchLeadsByStage: (stageId: StageId) => any;
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
    isCustomizingLeadCardFields: boolean;
};

export class VisualPipelineComponent extends React.PureComponent<Props, State> {
    headersScrollContainerRef: HTMLElement | null | undefined;
    tableScrollContainerRef: HTMLElement | null | undefined;

    constructor(props: Props) {
        super(props);

        this.state = {
            isCustomizingLeadCardFields: false,
        };
    }

    componentDidMount() {
        this.props.fetchLeadsSchema();
        this.props.fetchStages();
        this.props.fetchOutcomes();
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // If there's a new active stage transition (and there was not one
        // previously), scroll the board back to the top, where the confirmation
        // message shows up
        if (
            nextProps.activeStageTransition.id !== this.props.activeStageTransition.id &&
            this.props.activeStageTransition.id === null &&
            this.tableScrollContainerRef
        ) {
            // Just jump back to the top (it looks fine)
            this.tableScrollContainerRef.scrollTop = 0;
        }
    }

    render() {
        const containerStyle = {
            width: this.props.width,
            height: this.props.height,
        } as const;

        if (!this.props.stages.length) {
            return (
                <VisualPipelineEmptyContainer
                    errorMessage={this.props.errorMessage}
                    isLoading={this.props.isLoading}
                    onErrorRetry={this.handleErrorRetry}
                    // @ts-expect-error
                    onCloseStagesModal={this.props.fetchStages}
                    containerStyle={containerStyle}
                />
            );
        }

        // Measure scrollBar (if it exists)
        const scrollBarWidth = this.tableScrollContainerRef
            ? this.tableScrollContainerRef.offsetWidth - this.tableScrollContainerRef.clientWidth
            : 0;

        // Header width needs to equal the total width of the page,
        // minus the 16px left padding,
        // minus the scrollBar width of the scrollable table component below it
        const headerWidth = this.props.width - CONTENT_LEFT_PADDING - scrollBarWidth;

        return (
            <GetSessionUser>
                {({user}) => {
                    const canCustomizeFields =
                        this.props.stagesetId && user && user.isAdministrator;

                    return (
                        <div style={containerStyle} className={styles.container}>
                            <div
                                style={{width: headerWidth}}
                                className={styles['headers-container']}
                            >
                                <div
                                    ref={(node) => {
                                        this.headersScrollContainerRef = node;
                                    }}
                                    className={styles['headers-scroll-container']}
                                >
                                    {this.props.stages.map((stage, index) => {
                                        return (
                                            <VisualPipelineColumnHeader
                                                key={stage.id}
                                                stage={stage}
                                                zIndex={this.props.stages.length - index}
                                                isLastChild={index === this.props.stages.length - 1}
                                            />
                                        );
                                    })}
                                </div>
                                <SortAndCustomizeButton
                                    onCustomizeCards={
                                        canCustomizeFields
                                            ? () =>
                                                  this.setState({isCustomizingLeadCardFields: true})
                                            : undefined
                                    }
                                    activeSort={this.props.sort}
                                    onChangeSort={this.props.onToggleSort}
                                />
                            </div>
                            <div
                                ref={(node) => {
                                    this.tableScrollContainerRef = node;
                                }}
                                onScroll={this.handleScroll}
                                className={styles['scroll-container']}
                            >
                                <div className={styles['column-table']}>
                                    {this.props.stages.map((stage) => {
                                        return (
                                            <VisualPipelineColumn
                                                key={stage.id}
                                                search={this.props.search}
                                                selectedSort={this.props.sort.selected}
                                                filters={this.props.filters}
                                                stage={stage}
                                                fieldsToDisplay={this.props.fieldsToDisplay}
                                                isLoadingFields={this.props.isLoadingFields}
                                            />
                                        );
                                    })}
                                    <div
                                        className={
                                            canCustomizeFields
                                                ? styles['sort-by-spacer--with-customize']
                                                : styles['sort-by-spacer']
                                        }
                                    />
                                </div>
                            </div>
                            <VisualPipelineCloseLeadBar
                                filters={this.props.filters}
                                selectedSort={this.props.sort.selected}
                                isOpen={this.props.isCardBeingDragged}
                                fieldsToDisplay={this.props.fieldsToDisplay}
                            />
                            {this.props.isEmpty ? (
                                <div
                                    style={{width: this.props.stages.length * 300}}
                                    className={styles['empty-stages-overlay']}
                                >
                                    <StagesEmpty
                                        titleText='No leads found'
                                        bodyText='You currently don’t have any leads in this pipeline.'
                                    />
                                </div>
                            ) : undefined}
                            {this.props.isEmptyFiltered && !this.props.isEmpty ? (
                                <div
                                    style={{width: this.props.stages.length * 300}}
                                    className={styles['empty-stages-overlay']}
                                >
                                    <img
                                        src='/include/images/entity-lists/empty-search-transparent.png'
                                        className={styles['no-search-results']}
                                    />
                                </div>
                            ) : undefined}
                            {this.state.isCustomizingLeadCardFields && this.props.stagesetId ? (
                                <CustomizeLeadCardModal
                                    isOpen={true}
                                    onClose={() => {
                                        this.setState({isCustomizingLeadCardFields: false});
                                        this.handleRefetchAllCards(this.props.stages);
                                    }}
                                    stagesetId={this.props.stagesetId}
                                />
                            ) : undefined}
                            <div className={styles['default-pipeline-edit-cta']}>
                                <DefaultPipelineEditCTA
                                    completedWizardKey='defaultStagesetOnboardingWizardCTAOnLeadBoard'
                                    dismissKey='has_dismissed_stageset_wizard_cta_lead_board_NUT-8926'
                                />
                            </div>
                        </div>
                    );
                }}
            </GetSessionUser>
        );
    }

    /**
     * Callback when our card container (the board) has scrolled. We'll use this
     * event's scrollLeft value to keep our header in sync, as we want the header
     * to be fixed position, but still scroll left/right when the table below
     * is scrolled.
     * @param  {Event} e           - Event from react when the content is scrolled
     * @return {void}
     */
    handleScroll = (e: React.SyntheticEvent) => {
        // @ts-expect-error
        const {scrollHeight, offsetHeight, scrollTop, scrollLeft} = e.nativeEvent.target;

        if (this.headersScrollContainerRef) {
            this.headersScrollContainerRef.scrollLeft = scrollLeft;
        }

        const hasScrolledToBottom =
            scrollHeight - offsetHeight < scrollTop + SHOULD_LOAD_MORE_THRESHOLD;
        if (
            hasScrolledToBottom &&
            this.props.stagesWithMoreLeadsToLoad.length &&
            !this.props.isAnyColumnLoading
        ) {
            this.props.onLoadMoreLeads(this.props.stagesWithMoreLeadsToLoad);
        }
    };

    handleErrorRetry = () => {
        this.props.fetchStages();
    };

    handleRefetchAllCards = (stages: any[]) => {
        stages.forEach((stage) => this.props.fetchLeadsByStage(stage.id));
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const VisualPipeline = connector(VisualPipelineComponent);
